import type {
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
  SelectedScreenMeta,
  StepThreeData,
  StepTwoData,
} from '@/features/campaigns/types/campaign';

const STORAGE_KEY = 'screenista.standard.campaign.draft';

export type StandardCampaignDraft = {
  currentStep: number;
  campaignData: ManualCampaignForm;
  stepTwoData: StepTwoData;
  selectedScreensMeta: Record<string, SelectedScreenMeta>;
  submitData: StepThreeData;
  previewData: PreviewData;
  creativePreview: CreativePreview;
  skipScreenStep?: boolean;
  savedAt: number;
};

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function clampStep(step: unknown) {
  const n = Number(step);
  if (!Number.isFinite(n)) return 1;
  return Math.min(4, Math.max(1, Math.round(n)));
}

function slimPreview(preview: PreviewData | undefined): PreviewData {
  if (!preview || typeof preview !== 'object') return {};
  return {
    campaignId: preview.campaignId,
    campaignName: preview.campaignName,
    campaignGoals: preview.campaignGoals,
    startDate: preview.startDate,
    endDate: preview.endDate,
    customDuration: preview.customDuration,
    requiredOrientation: preview.requiredOrientation,
    pricing: preview.pricing,
    targetCities: preview.targetCities,
    analytics: preview.analytics
      ? {
          totalRepetitions: preview.analytics.totalRepetitions,
          totalDuration: preview.analytics.totalDuration,
          customDuration: preview.analytics.customDuration,
        }
      : undefined,
    billboardWise: Array.isArray(preview.billboardWise)
      ? preview.billboardWise.slice(0, 20)
      : preview.billboardWise,
  };
}

function readRaw(): string | null {
  const ls = storage();
  if (!ls) return null;
  try {
    const fromLs = ls.getItem(STORAGE_KEY);
    if (fromLs) return fromLs;
  } catch {
    // ignore
  }
  // Migrate older sessionStorage drafts if present
  try {
    const fromSs = window.sessionStorage.getItem(STORAGE_KEY);
    if (fromSs) {
      ls?.setItem(STORAGE_KEY, fromSs);
      window.sessionStorage.removeItem(STORAGE_KEY);
      return fromSs;
    }
  } catch {
    // ignore
  }
  return null;
}

export function loadStandardCampaignDraft(): StandardCampaignDraft | null {
  try {
    const raw = readRaw();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StandardCampaignDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.campaignData) return null;
    return {
      ...parsed,
      currentStep: clampStep(parsed.currentStep),
    };
  } catch {
    return null;
  }
}

function writePayload(payload: StandardCampaignDraft) {
  const ls = storage();
  if (!ls) return false;
  ls.setItem(STORAGE_KEY, JSON.stringify(payload));
  return true;
}

export function saveStandardCampaignDraft(
  draft: Omit<StandardCampaignDraft, 'savedAt'> & { savedAt?: number },
) {
  const creativePreview =
    draft.creativePreview?.url?.startsWith('blob:')
      ? { url: '', type: '' }
      : draft.creativePreview;

  const campaignData = draft.campaignData
    ? {
        ...draft.campaignData,
        mediaUrl: draft.campaignData.mediaUrl?.startsWith('blob:')
          ? undefined
          : draft.campaignData.mediaUrl,
        mediaIds: draft.campaignData.mediaIds?.map((m) => ({
          ...m,
          mediaUrl: m.mediaUrl?.startsWith('blob:') ? undefined : m.mediaUrl,
        })),
      }
    : draft.campaignData;

  // Prefer durable campaign media URL when blob preview was stripped
  const hydratedPreview =
    creativePreview?.url
      ? creativePreview
      : campaignData?.mediaUrl || campaignData?.mediaIds?.[0]?.mediaUrl
        ? {
            url:
              campaignData.mediaUrl ||
              campaignData.mediaIds?.[0]?.mediaUrl ||
              '',
            type:
              campaignData.mediaType ||
              campaignData.mediaIds?.[0]?.mediaType ||
              '',
          }
        : creativePreview;

  const base = {
    ...draft,
    campaignData,
    currentStep: clampStep(draft.currentStep),
    creativePreview: hydratedPreview,
    savedAt: draft.savedAt ?? Date.now(),
  } satisfies StandardCampaignDraft;

  try {
    writePayload({ ...base, previewData: slimPreview(draft.previewData) });
    return;
  } catch {
    // Quota — retry without preview / creative blobs
  }

  try {
    writePayload({
      ...base,
      previewData: {},
      creativePreview: { url: '', type: '' },
    });
  } catch {
    // ignore — still keep in-memory step during this session
  }
}

export function clearStandardCampaignDraft() {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
